import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AccountContext } from "../../contexts/AccountContexts";
import { ContentContext } from "../../contexts/ContentContext";
import { NotificationContext } from "../../contexts/NotificationContexts";
import { ConnectionLostMessage } from "../../components/ModalComponents";
import { UserMessageWritingComponent } from "../ticket";
import { formatTimestamp } from "../../utils";
import { ChatViewer } from "../../components/MessageComponents";
import {
  sendMessage,
  sendMessagesRead,
  sendMessageWithFileLinks,
} from "../../utils/messagingUtils";
import {
  requestMemberSubmitCompleteTicket,
  requestTicketMember,
} from "../../requests/ticket_requests";
import { statusToUserStatus } from "../../entries";
import { PageHeader } from "../../components/HeaderComponents";
import { loadingActive, pageLoadingActive } from "../../components/Background";
import { useConfirm } from "../../contexts/ModalContexts";

export const UserTicketPage = () => {
  const { ticket_info } = useParams();
  const { t } = useTranslation(["edit", "common"]);
  const getConfirmation = useConfirm();

  const { user, language } = useContext(AccountContext);
  const { socket, initializeSocket, reconnected } =
    useContext(NotificationContext);
  const { categories, deletedCategories, servers, openErrorModal } =
    useContext(ContentContext);

  const [ticketInfo, setTicketInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => {
    if (user && ticketInfo) {
      socket.emit("join_chatroom", {
        room_id: ticketInfo.chat_id,
        name: user.id,
      });
      sendMessagesRead({
        socket: socket,
        userId: user.id,
        chatId: ticketInfo.chat_id,
      });

      socket.on("ticket_assigned", (pckg) => {
        if (pckg.ticket.id !== ticketInfo.id) {
          return;
        }
        setTicketInformation({ ticketData: pckg.ticket });
      });

      return () => {
        socket.emit("leave_chatroom", {
          room_id: ticketInfo.chat_id,
          name: user.id,
        });
        socket.off("new_message");
      };
    }
  }, [loading, reconnected]);

  useEffect(() => {
    const decoded = JSON.parse(atob(ticket_info));
    loadingActive(true);
    requestTicketMember({ ticketId: decoded.ticketId })
      .then((data) => {
        setTicketInformation({ ticketData: data.ticket });
      })
      .catch((error) => {
        openErrorModal(error);
      })
      .finally(() => {
        loadingActive(false);
      });
  }, []);

  const setTicketInformation = ({ ticketData }) => {
    setTicketInfo(ticketData);
  };

  const handleSendMessage = (message, files) => {
    if (files.length > 0) {
      sendMessageWithFileLinks({
        socket: socket,
        message: message,
        files: files,
        ticketInfo: ticketInfo,
        user: user,
        replyTo: replyTo,
      }).then(() => {
        replyTo?.type === "message" && setReplyTo(null);
      });
    } else {
      sendMessage({
        socket: socket,
        message: message,
        ticketInfo: ticketInfo,
        user: user,
        replyTo: replyTo,
      }).then(() => {
        replyTo?.type === "message" && setReplyTo(null);
      });
    }
  };

  const handleResolveTicket = async () => {
    const confirmed = await getConfirmation(t("member_resolve_ticket_message"));
    if (confirmed) {
      loadingActive(true);
      requestMemberSubmitCompleteTicket({ ticketId: ticketInfo.id })
        .then((data) => {
          setTicketInformation({ ticketData: data.ticket });
        })
        .catch((error) => {
          openErrorModal(error);
        })
        .finally(() => {
          loadingActive(false);
        });
    }
  };

  if (user === null || ticketInfo === null) {
    return <></>;
  }
  return (
    <>
      <ConnectionLostMessage />
      <PageHeader pageTitle={t("Ticket") + " - " + ticketInfo.createdAt} />

      <div className="flex flex-col w-full min-h-screen bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 sm:p-5 rounded-xl shadow-2xl gap-4 pb-">
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 p-3 rounded-xl shadow-2xl gap-3 py-5">
          <h1 className="w-full text-center text-lg font-semibold">
            {t("Ticket Info")}
          </h1>
          <div className="flex flex-col sm:flex-row w-full gap-3">
            <div className="flex flex-col w-full gap-3">
              <h1 className="text-gray-800 dark:text-gray-300 font-semibold">
                {t("Ticket")} - {ticketInfo.createdAt}
              </h1>
              <div className="flex flex-col">
                {/* <span className="font-semibold">{ticketInfo.name}</span> */}
                <span className="">
                  {t("E-mail")} : {ticketInfo.email}
                </span>
              </div>

              <div className="flex flex-col gap-3">
                <span>
                  {t("Category")} :{" "}
                  <span className="font-semibold">
                    {categories[ticketInfo.category]
                      ? categories[ticketInfo.category]?.locales?.[language]
                        ? categories[ticketInfo.category]?.locales[language]
                        : t(categories[ticketInfo.category].title, {
                            ns: "common",
                          })
                      : deletedCategories[ticketInfo.category]
                        ? t(deletedCategories[ticketInfo.category].title, {
                            ns: "common",
                          })
                        : "UNK"}
                  </span>
                </span>
                <span className="truncate">
                  {t("Character Name")} :{" "}
                  <span className="font-semibold">
                    {ticketInfo.characterName}
                  </span>
                </span>
                {ticketInfo.violatersName &&
                  ticketInfo.violatersName !== "" && (
                    <span>
                      {t("Violater's Name")} :{" "}
                      <span className="font-semibold">
                        {ticketInfo.violatersName}
                      </span>
                    </span>
                  )}
                <span>
                  {t("Server")} :{" "}
                  <span className="font-semibold">
                    {servers[ticketInfo.server]?.title ??
                      servers[ticketInfo.server].name}
                  </span>
                </span>
              </div>

              <span>
                {t("Subject")}:{" "}
                <span className="font-semibold">{ticketInfo.subject}</span>
              </span>
            </div>

            <div className="flex flex-col gap-3 max-w-max w-max">
              <span className="flex gap-1 w-max">
                {t("Status")} :{" "}
                <span
                  className={`font-semibold ${statusToUserStatus[ticketInfo.status]?.style}`}
                >
                  {t(statusToUserStatus[ticketInfo.status]?.title ?? "UNK")}
                </span>
              </span>

              <a className="flex gap-1 w-max">
                {t("Created At")} :{" "}
                <span className="font-semibold">
                  {formatTimestamp(ticketInfo.createdAt * 1000)}
                </span>
              </a>

              <a className="flex gap-1 w-max">
                {t("Last Update")} :{" "}
                <span className="font-semibold">
                  {formatTimestamp(ticketInfo.lastUpdated * 1000)}
                </span>
              </a>
            </div>
          </div>

          {/* <div className="divider before:h-[1px] after:h-[1px]">{t('Description')}</div>
        
                            <div className="w-full h-40 sm:h-20 overflow-auto">
                                <span>{ticketInfo.description}</span>
        
        
                            </div>
                            <ImageViewer imageUrls={ticketInfo.attachments} /> */}
        </div>

        <ChatViewer
          key={ticketInfo.chat_id}
          socket={socket}
          ticketInfo={ticketInfo}
          chatId={ticketInfo.chat_id}
          firstPerson={user}
          anonUser={null}
          setAnonUser={() => {}}
          setTicketInformation={setTicketInformation}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
        />
        {ticketInfo.status !== "CLOSED" && ticketInfo.status !== "RESOLVED" ? (
          <UserMessageWritingComponent
            ticketInfo={ticketInfo}
            handleSendMessage={handleSendMessage}
            handleResolveTicket={handleResolveTicket}
            replyTo={replyTo}
            setReplyTo={setReplyTo}
          />
        ) : (
          <div className="divider"></div>
        )}
      </div>
    </>
  );
};
